import React, { useEffect, useState, useMemo} from "react";
import { Container, Row, Col, Card, CardBody, Badge, Button, Spinner } from "reactstrap";
import { useParams, Link, useNavigate } from "react-router-dom";
import { DashboardPageHeader , ColumnFilterRow, applyColumnFilters} from "../../Components/Common/DashboardKit";
import api from "../../api"; // adjust to your axios instance
import useAuth from "../Personal Attendance/useAuth";
import { approveEpo, rejectEpo, cancelEpo } from "../../services/epoApprovalsApi";
import EpoDecisionModal from "./EpoDecisionModal";
import SwapVendorModal from "../../Components/Epo/SwapVendorModal";  // R22 (batch1b)
// R47 — added downloadAttachment (auth-aware blob download).
import { listAttachments, attachmentDownloadUrl, downloadAttachment } from "../../services/epoVendorApi";



const statusBadge = (s) => {
    const v = (s || "").toUpperCase();
    // R33 — check FINANCE_EDIT first so it wins over the generic SUBMITTED
    // / APPROVED contains-match (FINANCE_EDIT contains neither but kept top for
    // discoverability).
    if (v === "FINANCE_EDIT" || v.includes("FINANCE_EDIT")) {
        return <Badge color="primary"><i className="ri-edit-2-line align-bottom me-1"></i>FINANCE EDIT</Badge>;
    }
    if (v.includes("APPROVED")) return <Badge color="success">APPROVED</Badge>;
    if (v.includes("REJECT")) return <Badge color="danger">REJECTED</Badge>;
    if (v.includes("PENDING")) return <Badge color="warning">PENDING</Badge>;
    if (v.includes("PENDING_APPROVAL")) return <Badge color="warning">PENDING</Badge>;
    if (v.includes("SUBMITTED")) return <Badge color="info">SUBMITTED</Badge>;
    if (v.includes("SUBMITTED REQUEST")) return <Badge color="info">SUBMITTED</Badge>;
    // R33 — cleaner fallback when status is empty / null than "UNKNOWN".
    return <Badge color="secondary">{v || "—"}</Badge>;
};

const TimelineIcon = (s) => {
    const v = (s || "").toUpperCase();
    if (v.includes("APPROVED")) return "ri-check-line";
    if (v.includes("REJECT")) return "ri-close-line";
    if (v.includes("PENDING")) return "ri-time-line";
    if (v.includes("SUBMITTED")) return "ri-send-plane-2-line";
    return "ri-information-line";
};

export default function EpoApprovalsTimeline() {
    const { epoNumber } = useParams();
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [current, setCurrent] = useState(null);
    // Owner fix (blank-page round 3) — surface EXACTLY what failed, and pull
    // the header independently so the status pill never blanks again.
    const [loadError, setLoadError] = useState("");
    const [headerInfo, setHeaderInfo] = useState(null);
    // R27 — payment cards list (fetched once); used to render a friendly
    // card name when the EPO has a Ccid (corporate-credit-card EPOs).
    const [cards, setCards] = useState([]);

    // Round 11 — approver actions on the timeline page itself, so the
    // "View details" link from the notification email is actionable.
    const auth = useAuth();

    // R27 — fetch the active payment cards list once so we can display
    // the friendly card name (legacy epoApprove.vb:137 — Label40.Text).
    useEffect(() => {
        let cancelled = false;
        api.get("registration/all")
            .then((res) => { if (!cancelled) setCards(Array.isArray(res.data) ? res.data : []); })
            .catch(() => { if (!cancelled) setCards([]); });
        return () => { cancelled = true; };
    }, []);

    const myEmpId = String(auth?.empId ?? "");
    // R17 — Finance/Babu/IT-Admin can edit any EPO's notes/lines/freight
    // and mark it as Processed/Paid. Surfaces a "Finance Edit" button below.
    const isFinanceFlavor = !!(auth?.isBabu || auth?.isFinance || auth?.isItAdmin);
    const navigate = useNavigate();
    const [actionMode, setActionMode] = useState(null); // null | "approve" | "reject" | "cancel"
    const [reloadKey, setReloadKey] = useState(0);
    // R22 (batch1b) — Swap Vendor modal open state
    const [swapOpen, setSwapOpen] = useState(false);
    // R23 (batch1b) — Attachments list (metadata only; download is a plain GET)
    const [attachments, setAttachments] = useState([]);
    // R43 — column-filter for the attachments mini-table.
    const [attFilters, setAttFilters] = useState({});
    const attColumns = useMemo(() => [
        { id: "id",        label: "ID",       type: "text",
          get: a => String(a.Id ?? a.id ?? "") },
        { id: "fileName",  label: "Filename", type: "text",
          get: a => a.FileName ?? a.fileName ?? "" },
        { id: "size",      label: "Size",     type: "none" },
        { id: "uploaded",  label: "Uploaded", type: "text",
          get: a => {
              const ts = a.Timestamp ?? a.timestamp;
              try { return ts ? new Date(ts).toLocaleString() : ""; } catch { return ""; }
          }},
        { id: "actions",   label: "",         type: "none" },
    ], []);
    const filteredAttachments = useMemo(
        () => applyColumnFilters(attachments, attColumns, attFilters),
        [attachments, attColumns, attFilters]
    );


    useEffect(() => {
        if (!epoNumber) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setLoadError("");
            try {
                // Owner fix — allSettled + independent header fetch: one failing
                // call can no longer blank the whole page, and whatever DOES
                // fail is reported on-screen with its status and URL.
                const [histSet, curSet, hdrSet] = await Promise.allSettled([
                    api.get(`epo-approvals/${epoNumber}/history`),
                    api.get(`epo-approvals/${epoNumber}/current`),
                    api.get(`Epo/${epoNumber}`),
                ]);

                if (!cancelled && hdrSet.status === "fulfilled") {
                    const d = hdrSet.value?.data;
                    setHeaderInfo(d?.header ?? d?.Header ?? d ?? null);
                }
                if (histSet.status === "rejected" || curSet.status === "rejected") {
                    const bad = histSet.status === "rejected" ? histSet.reason : curSet.reason;
                    const st = bad?.response?.status ?? "";
                    const url = bad?.config ? `${bad.config.baseURL || ""}${bad.config.url || ""}` : "";
                    if (!cancelled) setLoadError(`Timeline fetch failed${st ? ` (HTTP ${st})` : ""}: ${url} — ${bad?.response?.data?.error || bad?.message || "unknown error"}`);
                }

                const histRes = histSet.status === "fulfilled" ? histSet.value : { data: [] };
                const curRes = curSet.status === "fulfilled" ? curSet.value : { data: null };
                // R23 (batch1b) — also load attachments. Independent of approval data;
                // swallow failures so the timeline still loads if the list errors.
                try {
                    const a = await listAttachments(epoNumber);
                    if (!cancelled) setAttachments(Array.isArray(a) ? a : []);
                } catch { if (!cancelled) setAttachments([]); }

                if (cancelled) return;

                // history might be an array OR wrapped
                const historyRows = Array.isArray(histRes.data)
                    ? histRes.data
                    : (histRes.data?.rows ?? histRes.data?.results ?? []);

                setRows(historyRows);

                // current should be an object
                setCurrent(curRes.data || null);

            } catch (err) {
                console.error("Timeline load failed", err);
                if (cancelled) return;
                setRows([]);
                setCurrent(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [epoNumber, reloadKey]);

    // Find an ACTIVE pending row assigned to me on this EPO. If present,
    // the page renders Approve/Reject buttons. Casing-tolerant access
    // because some endpoints serialize PascalCase, others camelCase.
    const myPendingRow = (rows || []).find((r) => {
        const active = r.active ?? r.Active;
        const status = String(r.status ?? r.Status ?? "").toUpperCase();
        const approverEmpId = r.approverEmpId ?? r.ApproverEmpId;
        return active && status === "PENDING_APPROVAL"
            && String(approverEmpId ?? "") === myEmpId
            && myEmpId !== "";
    });

    // Round 15 — the creator (requester) is whichever empId filed the
    // SUBMITTED REQUEST history row. The Cancel button appears only when the
    // viewer matches that empId AND the EPO is still PENDING (pre-terminal).
    const creatorEmpId = (() => {
        const submitted = (rows || []).find(
            (r) => String(r.status ?? r.Status ?? "").toUpperCase().includes("SUBMITTED")
        );
        return String(submitted?.empId ?? submitted?.EmpId ?? "");
    })();
    const isCreator = creatorEmpId !== "" && creatorEmpId === myEmpId;
    const canCancel = isCreator && /PENDING/i.test(
        String(current?.currentStatus ?? current?.CurrentStatus ?? current?.status ?? current?.Status ?? "")
    );

    // Round 15 — creator-cancel dispatches a new API call.
    const handleConfirm = async ({ epoNumber: en, comment }) => {
        if (actionMode === "approve") {
            await approveEpo(en, comment || "");
        } else if (actionMode === "reject") {
            await rejectEpo(en, comment || "");
        } else if (actionMode === "cancel") {
            await cancelEpo(en, comment || "");
        }
        setActionMode(null);
        setReloadKey((k) => k + 1);
    };

    const currentStatus =
        current?.currentStatus ??
        current?.CurrentStatus ??
        current?.status ??
        current?.Status ??
        current?.finalStatus ??
        current?.FinalStatus ??
        "";

    const currentApprover =
        current?.currentApproverEmpId ??
        current?.CurrentApproverEmpId ??
        current?.approverEmpId ??
        current?.ApproverEmpId ??
        null;

    // Owner fix — /current is NULL once the EPO is terminal (APPROVED /
    // DENIED / CANCELLED): derive the status pill and approver from the
    // HISTORY rows instead of a blank pill and "(none)".
    const activeRow = (rows || []).find((r) => (r.active ?? r.Active));
    const lastRow = (rows || []).length ? rows[rows.length - 1] : null;
    const shownStatus = currentStatus
        || (activeRow ? "PENDING_APPROVAL" : String(lastRow?.status ?? lastRow?.Status ?? ""))
        || String(headerInfo?.status ?? headerInfo?.Status ?? "");
    const nameCell = (nm, id) => (nm ? `${nm} (${id ?? ""})`.replace(" ()", "") : (id != null && id !== "" ? String(id) : null));
    const shownApprover = currentApprover != null
        ? String(currentApprover)
        : (activeRow ? nameCell(activeRow.approverName ?? activeRow.ApproverName, activeRow.approverEmpId ?? activeRow.ApproverEmpId) : null);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-history-line"
                    eyebrow="EPO"
                    title="Approval History"
                    subtitle="Full decision timeline for this expense purchase order."
                    tone="amber"
                    breadcrumb={[
                        { label: "Dashboard", to: "/" },
                        { label: "EPO", to: "/workflows?wf=epo" },
                        { label: "Approval History" },
                    ]}
                />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                {loading ? (
                                    <div className="text-center py-4 text-muted"><Spinner size="sm" className="me-2" />Loading timeline…</div>
                                ) : (
                                    <>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div>
                                                <div className="text-muted">
                                                    EPO #{epoNumber}
                                                    {headerInfo && (
                                                        <span className="ms-2">
                                                            — created by {String(headerInfo.createdBy ?? headerInfo.CreatedBy ?? "?")}, total {Number(headerInfo.approxTotal ?? headerInfo.ApproxTotal ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" })}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-1">
                                                    <strong>Current Status:</strong>{" "}
                                                    {statusBadge(shownStatus)}
                                                    <span className="ms-3">
                                                        <strong>Current Approver:</strong>{" "}
                                                        {shownApprover ?? "(none)"}
                                                    </span>
                                                </div>
                                                {/* R27 — payment card display when EPO uses corporate credit card. */}
                                                {(() => {
                                                    const ccid = current?.epoHeader?.ccid ?? current?.epoHeader?.Ccid;
                                                    if (!ccid) return null;
                                                    const card = cards.find((c) => String(c.id) === String(ccid));
                                                    const label = card?.printValue || card?.PrintValue
                                                        || (card ? `${card.nameOnCard || card.NameOnCard} **${card.lastDigits || card.LastDigits}` : `Card #${ccid}`);
                                                    return (
                                                        <div className="mt-1 text-muted small">
                                                            <strong>Payment Card:</strong>{" "}{label}
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            <div className="d-flex gap-2 flex-wrap">
                                                {myPendingRow && (
                                                    <>
                                                        <Button color="success" size="sm" onClick={() => setActionMode("approve")}>
                                                            <i className="ri-check-line align-bottom me-1"></i>Approve
                                                        </Button>
                                                        <Button color="danger" size="sm" onClick={() => setActionMode("reject")}>
                                                            <i className="ri-close-line align-bottom me-1"></i>Reject
                                                        </Button>
                                                    </>
                                                )}
                                                {/* Round 15 — creator-cancel shows when the viewer is the original
                                                    requester (found via the SUBMITTED REQUEST history row) and the
                                                    EPO is still PENDING. */}
                                                {canCancel && (
                                                    <Button color="warning" size="sm" onClick={() => setActionMode("cancel")}>
                                                        <i className="ri-prohibited-line align-bottom me-1"></i>Cancel EPO
                                                    </Button>
                                                )}
                                                {/* R17 — Finance / IT-Admin entry into the edit form. Lets them
                                                    edit notes, miles, freight, line items, and mark the EPO as
                                                    Processed/Paid. Mirrors legacy epoApprove.vb babuMode. */}
                                                {isFinanceFlavor && (
                                                    <Button
                                                        color="info"
                                                        size="sm"
                                                        onClick={() => navigate(`/epo?mode=edit&epoNumber=${epoNumber}`)}
                                                    >
                                                        <i className="ri-edit-2-line align-bottom me-1"></i>Finance Edit
                                                    </Button>
                                                )}
                                                {/* R19 — Partial Payments page entry. Same role gate as Finance Edit. */}
                                                {isFinanceFlavor && (
                                                    <Button
                                                        color="success"
                                                        size="sm"
                                                        onClick={() => navigate(`/epo/${epoNumber}/partial-payments`)}
                                                    >
                                                        <i className="ri-money-dollar-circle-line align-bottom me-1"></i>Partial Payments
                                                    </Button>
                                                )}
                                                {/* R27 — jump to the receive page (parity with legacy epoApprove.btnReceive). */}
                                                {/* Owner authz fix — Receive is Finance / IT-Admin only,
                                                    same gate as Finance Edit (server enforces too). */}
                                                {isFinanceFlavor && (
                                                    <Link to={`/epo/${epoNumber}/receive`} className="btn btn-light btn-sm">
                                                        <i className="ri-archive-line align-bottom me-1"></i>Receive Items
                                                    </Link>
                                                )}
                                                <Link to="/epo" className="btn btn-light btn-sm">
                                                    Back to EPO List
                                                </Link>
                                            </div>
                                        </div>

                                        <div className="timeline">
                                            {loadError && (
                                                <div className="alert alert-danger" role="alert" style={{ wordBreak: "break-all" }}>
                                                    {loadError}
                                                </div>
                                            )}
                                            {!loadError && rows.length === 0 && (
                                                <div className="text-center text-muted py-4">
                                                    {String(shownStatus).toUpperCase().includes("APPROV")
                                                        ? "This EPO was auto-approved within the requester's limit — no approver steps were required."
                                                        : "No approval activity found for this EPO — it has not been submitted for approval yet (drafts have no timeline)."}
                                                </div>
                                            )}
                                            {rows.map((r, idx) => (
                                                <div key={r.id ?? idx} className={`timeline-item ${idx % 2 === 0 ? "left" : "right"}`}>
                                                    <i className="icon ri-stack-line"></i>
                                                    {/* <i className={`icon ${TimelineIcon(r.status)}`}></i> */}

                                                    <div className="date">
                                                        {new Date(r.stamp).toLocaleString()}
                                                    </div>

                                                    <div className="content">
                                                        <h5 className="fs-15 mb-2">
                                                            {r.status}
                                                            <small className="text-muted fs-13 fw-normal ms-2">
                                                                - Step {r.stepNumber ?? "-"}
                                                            </small>
                                                        </h5>

                                                        <p className="text-muted mb-2">
                                                            Requester: {r.requesterName ? `${r.requesterName} (${r.empId ?? ""})` : (r.empId ?? "-")} <br />
                                                            Approver: {r.approverName ? `${r.approverName} (${r.approverEmpId ?? ""})` : (r.approverEmpId ?? "-")} <br />
                                                            Actor: {r.actorName ? `${r.actorName} (${r.actorEmpId ?? ""})` : (r.actorEmpId ?? "-")} <br />
                                                            Amount: {r.amount != null ? Number(r.amount).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "-"}
                                                        </p>

                                                        {r.comment ? (
                                                            <p className="text-muted fst-italic mb-0">"{r.comment}"</p>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* R23 — Attachments list. Always visible to anyone who can
                    see the timeline. Download is a plain GET so the browser handles save-as. */}
                {attachments && attachments.length > 0 && (
                    <Row className="mt-3">
                        <Col xs={12}>
                            <Card>
                                <CardBody>
                                    <h5 className="mb-3">
                                        <i className="ri-attachment-2 align-bottom me-2"></i>
                                        Attachments ({attachments.length})
                                    </h5>
                                    <div className="table-responsive epo-sticky-head">
                                        {/* R43 — attachments column-filter row (consistent with R41/R42). */}
                                        <table className="table table-striped mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: 60 }}>ID</th>
                                                    <th>Filename</th>
                                                    <th style={{ width: 110 }} className="text-end">Size</th>
                                                    <th style={{ width: 180 }}>Uploaded</th>
                                                    <th style={{ width: 130 }}></th>
                                                </tr>
                                                <ColumnFilterRow
                                                    columns={attColumns}
                                                    filters={attFilters}
                                                    onChange={setAttFilters}
                                                />
                                            </thead>
                                            <tbody>
                                                {filteredAttachments.map((a) => {
                                                    const id = a.Id ?? a.id;
                                                    const fn = a.FileName ?? a.fileName ?? `attachment-${id}`;
                                                    const size = Number(a.SizeBytes ?? a.sizeBytes ?? 0);
                                                    const sizeLabel = size > 1048576
                                                        ? `${(size/1048576).toFixed(1)} MB`
                                                        : size > 1024
                                                            ? `${(size/1024).toFixed(0)} KB`
                                                            : `${size} B`;
                                                    const ts = a.Timestamp ?? a.timestamp;
                                                    let when = "—";
                                                    try { if (ts) when = new Date(ts).toLocaleString(); } catch {}
                                                    return (
                                                        <tr key={id}>
                                                            <td>{id}</td>
                                                            <td style={{ wordBreak: "break-all" }}>{fn}</td>
                                                            <td className="text-end">{sizeLabel}</td>
                                                            <td>{when}</td>
                                                            <td>
                                                                {/* R47 — was <a href={attachmentDownloadUrl(...)}>.
                                                                    Plain navigation drops the JWT Authorization header,
                                                                    so the [Authorize] endpoint returned 401 ("File
                                                                    wasn't available on site" in Chrome). Now fetch
                                                                    the bytes via axios and trigger the download from
                                                                    a temporary blob URL. */}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary btn-sm"
                                                                    onClick={async () => {
                                                                        try {
                                                                            await downloadAttachment(epoNumber, id, fn);
                                                                        } catch (e) {
                                                                            console.error("Attachment download failed:", e);
                                                                            alert(`Download failed: ${e?.response?.status === 404 ? "attachment not found" : (e?.message || "unknown error")}`);
                                                                        }
                                                                    }}
                                                                >
                                                                    <i className="ri-download-2-line align-bottom me-1"></i>
                                                                    Download
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                <EpoDecisionModal
                    isOpen={actionMode !== null}
                    toggle={() => setActionMode(null)}
                    action={actionMode ?? "approve"}
                    epoNumber={Number(epoNumber)}
                    onConfirm={handleConfirm}
                />

                {/* R22 — Swap Vendor modal */}
                <SwapVendorModal
                    isOpen={swapOpen}
                    toggle={() => setSwapOpen(false)}
                    epoNumber={Number(epoNumber)}
                    currentVendor={current?.epoHeader?.Company ?? current?.epoHeader?.company ?? ""}
                    onSwapped={() => setReloadKey(k => k + 1)}
                />
            </Container>
        </div>
    );
}

